import { nanoid } from "nanoid";
import { parse as parseToml } from "smol-toml";
import { normaliseColor } from "./colors";
import { ExpectedError } from "./expectedError";
import { hasConflictMarkers, keepOurs } from "./threeWayMerge";

export type StepConnector = { name: string; type: string; note?: string };

export const ROUTE_TYPES = ["webpage", "api", "webhook", "other"] as const;
export type RouteType = (typeof ROUTE_TYPES)[number];

export function isRouteType(v: unknown): v is RouteType {
  return (
    typeof v === "string" && (ROUTE_TYPES as readonly string[]).includes(v)
  );
}

export function isWebpageRoute(v: unknown): boolean {
  return v === "webpage";
}

export function pickPrimaryRoute<
  T extends { route: string; stepDir: string; routeType?: string | null },
>(routes: readonly T[], preferStepDir?: string | null): T | null {
  if (routes.length === 0) return null;
  const sorted = [...routes].sort(
    (a, b) =>
      a.route.localeCompare(b.route) || a.stepDir.localeCompare(b.stepDir)
  );
  return (
    (preferStepDir
      ? sorted.find((r) => r.stepDir === preferStepDir)
      : undefined) ??
    sorted.find((r) => isWebpageRoute(r.routeType)) ??
    sorted[0] ??
    null
  );
}

// Route_auth values for routes exposed to other workflows through a
// workflow-backed connector. `connector` is canonical; `connected_app` is the
// legacy name still present in stored configs until it's fully retired.
const CONNECTOR_ROUTE_AUTHS = ["connector", "connected_app"];

export function isConnectorRouteAuth(routeAuth: string | undefined): boolean {
  return routeAuth !== undefined && CONNECTOR_ROUTE_AUTHS.includes(routeAuth);
}

// Auth modes that let a caller reach a route without holding space:read on the
// route's space. A draft (non-main branch) preview must never be reachable
// through one of these, so its effective auth is floored to "space".
const NON_SPACE_ROUTE_AUTHS = new Set([
  "public",
  "external_id",
  "sso",
  "tenant",
  ...CONNECTOR_ROUTE_AUTHS,
]);

// A draft branch's route is served under the stricter of its configured auth
// and "space", so an unpushed draft always requires space membership even when
// the step's config.toml declares a weaker mode.
export function draftRouteAuth(routeAuth: string): string {
  return NON_SPACE_ROUTE_AUTHS.has(routeAuth) ? "space" : routeAuth;
}

export type StepConfig = {
  color: string;
  output: boolean;
  route?: string;
  routeAuth?: string;
  routeIdp?: string;
  routeGroups?: string[];
  routeType?: RouteType;
  externalId?: string;
  title?: string;
  emailAddress?: string;
  cron?: string;
  links?: string[];
  connectors?: StepConnector[];
  timeout?: number;
  retrySeconds?: number[];
  pinned?: boolean;
};

const MAX_STEP_RETRIES = 10;
const MAX_STEP_RETRY_SECONDS = 30 * 60;

function parseStepRetrySeconds(value: unknown): number[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    throw new ExpectedError("retry_seconds must be an array of seconds.");
  }
  if (value.length > MAX_STEP_RETRIES) {
    throw new ExpectedError(
      `retry_seconds supports at most ${MAX_STEP_RETRIES} retries.`
    );
  }
  if (
    !value.every(
      (seconds) =>
        typeof seconds === "number" &&
        Number.isSafeInteger(seconds) &&
        seconds >= 1 &&
        seconds <= MAX_STEP_RETRY_SECONDS
    )
  ) {
    throw new ExpectedError(
      `retry_seconds values must be whole seconds from 1 to ${MAX_STEP_RETRY_SECONDS}.`
    );
  }
  return [...value] as number[];
}

function parseRouteGroups(value: unknown): string[] {
  if (value === undefined) return [];
  if (
    !Array.isArray(value) ||
    value.length === 0 ||
    !value.every((name) => typeof name === "string" && name.trim() !== "")
  ) {
    throw new ExpectedError(
      "route_sso_groups must be a non-empty array of group names."
    );
  }
  return [...new Set(value.map((name: string) => name.trim()))];
}

// Restrictive on purpose — dodges quoting/encoding edge cases in SMTP and keeps addresses memorable.
const EMAIL_LOCALPART_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export function isValidEmailLocalPart(v: string): boolean {
  return v.length > 0 && v.length <= 64 && EMAIL_LOCALPART_RE.test(v);
}

/**
 * Routes are authenticated by default. A step with a `route` but no
 * `route_auth` is treated as space-scoped. `external_id` auth needs an
 * unguessable id; if the caller didn't supply one, mint it here so the AI
 * can't pick a guessable value.
 */
export function enforceRouteAuth(c: StepConfig): StepConfig {
  if (c.route && !c.routeAuth) {
    return { ...c, routeAuth: "space" };
  }
  if (c.routeAuth === "external_id" && !c.externalId) {
    return { ...c, externalId: nanoid() };
  }
  return c;
}

export function formatStepConnectorsLine(connectors: StepConnector[]): string {
  return `connectors = [${connectors.map((a) => `{ name = ${JSON.stringify(a.name)}, type = ${JSON.stringify(a.type)}${a.note ? `, note = ${JSON.stringify(a.note)}` : ""} }`).join(", ")}]`;
}

export function composeStepConfig(c: StepConfig): string {
  c = enforceRouteAuth(c);
  const lines = [`color = ${JSON.stringify(c.color)}`];
  if (c.output) lines.push("output = true");
  if (c.route) lines.push(`route = ${JSON.stringify(c.route)}`);
  if (c.routeAuth) {
    const value =
      c.routeAuth === "external_id" && c.externalId
        ? `external_id:${c.externalId}`
        : c.routeAuth;
    lines.push(`route_auth = ${JSON.stringify(value)}`);
  }
  if (c.routeIdp) lines.push(`route_idp = ${JSON.stringify(c.routeIdp)}`);
  if (c.routeGroups?.length)
    lines.push(`route_sso_groups = ${JSON.stringify(c.routeGroups)}`);
  if (c.routeType) lines.push(`route_type = ${JSON.stringify(c.routeType)}`);
  if (c.title) lines.push(`title = ${JSON.stringify(c.title)}`);
  if (c.emailAddress)
    lines.push(`email_address = ${JSON.stringify(c.emailAddress)}`);
  if (c.pinned) lines.push("pinned = true");
  if (c.cron) lines.push(`cron = ${JSON.stringify(c.cron)}`);
  if (c.links?.length)
    lines.push(`links = [${c.links.map((l) => JSON.stringify(l)).join(", ")}]`);
  if (c.connectors?.length) lines.push(formatStepConnectorsLine(c.connectors));
  if (c.timeout !== undefined) lines.push(`timeout = ${c.timeout}`);
  if (c.retrySeconds?.length) {
    lines.push(`retry_seconds = [${c.retrySeconds.join(", ")}]`);
  }
  return `${lines.join("\n")}\n`;
}

function parseConnectors(arr: unknown[]): StepConnector[] {
  const connectors: StepConnector[] = [];
  for (const item of arr) {
    if (typeof item === "string") {
      connectors.push({ name: item, type: item });
    } else if (item && typeof item === "object") {
      const { name, type, note } = item as Record<string, unknown>;
      if (typeof name === "string" && typeof type === "string") {
        connectors.push({
          name,
          type,
          ...(typeof note === "string" && { note }),
        });
      }
    }
  }
  return connectors;
}

export function scrubAiExternalId(
  afterToml: string,
  beforeToml: string | undefined
): string {
  const after = parseStepConfig(afterToml);
  if (after.routeAuth !== "external_id") return afterToml;
  // A before-image mid-merge still holds the step’s id on its branch side.
  const before = beforeToml ? displayStepConfig(beforeToml) : null;
  const previousId =
    before?.routeAuth === "external_id" ? before.externalId : undefined;
  if (previousId && after.externalId === previousId) return afterToml;
  return composeStepConfig({ ...after, externalId: previousId });
}

export function defaultStepConfig(): StepConfig {
  return { color: "sky", output: false };
}

// For callers that pick their own fallback for an unparseable config.toml;
// write and execute paths use parseStepConfig and refuse.
export function tryParseStepConfig(raw: string): StepConfig | null {
  try {
    return parseStepConfig(raw);
  } catch {
    return null;
  }
}

// Renders a step mid-merge as it looked before the merge. Facet sync must not
// use this: held-back facets are what keep a conflicted step inert.
export function displayStepConfig(raw: string): StepConfig {
  return (
    tryParseStepConfig(raw) ??
    tryParseStepConfig(keepOurs(raw)) ??
    defaultStepConfig()
  );
}

export function parseStepConfig(raw: string): StepConfig {
  let doc: Record<string, unknown>;
  try {
    doc = parseToml(raw);
  } catch (error) {
    if (hasConflictMarkers(raw))
      throw new ExpectedError("config.toml has unresolved merge conflicts");
    throw new ExpectedError(
      `Invalid config.toml: ${error instanceof Error ? error.message : String(error)}`
    );
  }
  const c: StepConfig = defaultStepConfig();
  if (typeof doc.color === "string") c.color = normaliseColor(doc.color);
  if (doc.output === true) c.output = true;
  if (typeof doc.route === "string") c.route = doc.route;
  if (typeof doc.route_auth === "string") {
    const colon = doc.route_auth.indexOf(":");
    if (colon !== -1) {
      c.routeAuth = doc.route_auth.slice(0, colon);
      c.externalId = doc.route_auth.slice(colon + 1);
    } else {
      c.routeAuth = doc.route_auth;
    }
    // "oauth" is a retired value: OAuth bearers are a credential the scoped
    // bars accept, not a bar of their own. Blobs are immutable, so stored
    // configs are normalized here rather than rewritten.
    if (c.routeAuth === "oauth") c.routeAuth = "space";
  }
  if (typeof doc.route_idp === "string" && doc.route_idp)
    c.routeIdp = doc.route_idp;
  const routeGroups = parseRouteGroups(doc.route_sso_groups);
  if (routeGroups.length > 0) c.routeGroups = routeGroups;
  if (isRouteType(doc.route_type)) c.routeType = doc.route_type;
  if (typeof doc.title === "string") {
    const title = doc.title.trim();
    if (title) c.title = title.slice(0, 200);
  }
  if (typeof doc.email_address === "string") {
    const parsed = doc.email_address.toLowerCase();
    if (isValidEmailLocalPart(parsed)) c.emailAddress = parsed;
  }
  if (doc.pinned === true) c.pinned = true;
  if (typeof doc.cron === "string") c.cron = doc.cron;
  if (Array.isArray(doc.links)) {
    const links = doc.links.filter((l): l is string => typeof l === "string");
    const dupe = links.find((l, i) => links.indexOf(l) !== i);
    if (dupe)
      throw new ExpectedError(`Step links to "${dupe}" more than once.`);
    c.links = links;
  }
  // `connectors` is the current key; `apps` is the legacy name still present in
  // stored configs, read here until it's fully retired.
  if (Array.isArray(doc.connectors)) {
    c.connectors = parseConnectors(doc.connectors);
  } else if (Array.isArray(doc.apps)) {
    c.connectors = parseConnectors(doc.apps);
  }
  if (typeof doc.timeout === "number") c.timeout = doc.timeout;
  if (doc.retry_seconds !== undefined) {
    c.retrySeconds = parseStepRetrySeconds(doc.retry_seconds);
  }
  return c;
}

/**
 * Step directory for a commit file path, or null for non-step files.
 * Prefer `row.stepDir` when you have a `commit_files` row; this exists
 * for callers that process raw paths pre-row (git push ingestion).
 */
export function stepDirFromPath(path: string): string | null {
  if (path.startsWith(".notes/")) return null;
  const slash = path.indexOf("/");
  return slash === -1 ? null : path.substring(0, slash);
}

/**
 * A path with no step directory is only legitimate at two reserved locations:
 * the workflow-level `README.md` (shown on the Readme tab) and the agent's
 * `.notes/` scratchpad. Every other file must live under a step, since the UI
 * can neither view nor link to a stray file at the workflow root.
 */
export function isAllowedNonStepPath(path: string): boolean {
  return path === "README.md" || path.startsWith(".notes/");
}

export function isScratchStep(dir: string): boolean {
  return dir.startsWith("__scratch_");
}

export const STEP_TESTS_DIR = "tests";

/**
 * A step's test files live under `<stepDir>/tests/`. They travel with the step
 * as commit files but are excluded from the build/run context and content hash,
 * so editing a test never busts the build cache or breaks upstream matching.
 */
export function isStepTestPath(path: string): boolean {
  const dir = stepDirFromPath(path);
  return dir !== null && path.startsWith(`${dir}/${STEP_TESTS_DIR}/`);
}
